/**
 * Login pelo Keycloak — Authorization Code + PKCE, escrito à mão.
 *
 * **Por que sem lib.** `oidc-client-ts` e `keycloak-js` resolvem muito
 * mais do que a gente usa (iframe de sessão, múltiplos provedores,
 * armazenamento plugável) e trazem junto o que a gente NÃO quer: os dois
 * guardam token em `localStorage`/`sessionStorage` por default, que é
 * exatamente a pendência 5 desta entrega. O que sobra do padrão, tirando
 * isso, cabe em um arquivo: montar duas URLs, um POST de formulário e
 * uma comparação de `state`.
 *
 * **O que fica no navegador, e o que não fica.** Só o `code_verifier` e
 * o `state` vão pro `sessionStorage`, porque precisam atravessar o
 * redirect — são de uso único, valem segundos e não abrem sessão
 * nenhuma sozinhos. **Token nenhum é persistido**: access e refresh
 * vivem em memória (ver `session.ts`), e quem sustenta a sessão entre
 * recarregamentos é o cookie de SSO do Keycloak, que é `HttpOnly` e
 * portanto invisível pra XSS. É a diferença que a pendência 5 pede: com
 * token no `localStorage`, um XSS rouba a sessão inteira e a leva
 * embora; sem ele, o roubo dura o que durar a página aberta.
 *
 * **Recarregar não mostra tela de login.** O bootstrap tenta primeiro um
 * `prompt=none` — o Keycloak responde com um código novo se o cookie de
 * SSO ainda valer, e devolve `login_required` se não. É redirect de
 * página inteira, e não iframe escondido, porque iframe depende de
 * cookie de terceiro em contexto embutido, que os navegadores estão
 * fechando: a versão com iframe funcionaria hoje e quebraria sozinha,
 * sem ninguém ter mexido em nada.
 *
 * **O `id_token` é decodificado, nunca validado aqui.** Serve pra
 * mostrar nome e e-mail na topbar. Quem confere assinatura, emissor,
 * audiência e validade é o `web_api` (`infrastructure/keycloak.py`), e
 * essa é a única checagem que vale: uma validação no navegador seria
 * feita com dados que o próprio navegador poderia ter adulterado.
 * Papel também vem do servidor, no `403` que ele dá — a lista de papéis
 * daqui só decide o que a tela OFERECE.
 */

const ISSUER = (import.meta.env.VITE_KEYCLOAK_ISSUER as string | undefined)?.replace(/\/$/, "") || null;
const CLIENT_ID = (import.meta.env.VITE_KEYCLOAK_CLIENT_ID as string | undefined) || null;
const ROLES_CLIENT_ID =
  (import.meta.env.VITE_KEYCLOAK_ROLES_CLIENT_ID as string | undefined) || null;

/** Ligado só quando as TRÊS variáveis existem, pelo mesmo motivo do
 *  servidor: meia configuração produz um login que começa e não termina. */
export const oidcEnabled = Boolean(ISSUER && CLIENT_ID && ROLES_CLIENT_ID);

/** `openid` é obrigatório pra vir `id_token`; `profile`/`email` são o
 *  nome e o e-mail da topbar. Nada além disso — escopo a mais é consentimento
 *  a mais numa tela que a pessoa não deveria precisar ler. */
const SCOPE = "openid profile email";

const VERIFIER_KEY = "reputaition.oidc.verifier";
const STATE_KEY = "reputaition.oidc.state";
const NONCE_KEY = "reputaition.oidc.nonce";
const SILENT_TRIED_KEY = "reputaition.oidc.silent_tried";
const RETURN_TO_KEY = "reputaition.oidc.return_to";
const SWITCH_ACCOUNT_KEY = "reputaition.oidc.switch_account";
/** No `localStorage`, e não no `sessionStorage`: precisa sobreviver ao
 *  navegador fechado, que é justamente quando a entrada automática pelo
 *  Google (2026-10-21) faz diferença. Ver `rememberLoginHint`. */
const LOGIN_HINT_KEY = "reputaition.oidc.login_hint";

/** A rota que o realm tem em `redirectUris`. Fixa, e não a página atual:
 *  o Keycloak compara com a lista cadastrada, e uma URL por tela viraria
 *  uma lista impossível de manter (e frouxa por consequência). */
export const REDIRECT_PATH = "/auth/callback";

export type TokenSet = {
  accessToken: string;
  refreshToken: string | null;
  idToken: string | null;
  /** Instante (ms) em que o access token vence, já com folga aplicada. */
  expiresAt: number;
};

/** As ações que a tela de Segurança pede ao realm. São as *required
 *  actions* padrão do Keycloak, que precisam estar habilitadas no realm
 *  (KEYCLOAK.md). `webauthn-register-passwordless` cadastra a chave de
 *  acesso (passkey) que entra sem senha e sem código (2026-10-21). */
export type AccountAction = "UPDATE_PASSWORD" | "CONFIGURE_TOTP" | "webauthn-register-passwordless";

export type IdentityClaims = {
  sub: string;
  name: string | null;
  email: string | null;
  roles: string[];
};

/** Erro de fluxo OIDC — separado de `ApiError` porque não é resposta da
 *  nossa API, e o tratamento é outro (mandar pro login, não avisar). */
export class OidcError extends Error {
  constructor(message: string, readonly code: string | null = null) {
    super(message);
  }
}

/** O usuário tem sessão no Keycloak, mas não neste navegador agora — a
 *  resposta esperada do `prompt=none` quando ninguém está logado. Não é
 *  falha: é o caminho normal pra tela de login. */
export const LOGIN_REQUIRED = "login_required";

/** As recusas do `prompt=none` que são desfecho normal, e não falha. Com a
 *  entrada automática pelo Google (2026-10-21), o Keycloak repassa o
 *  `prompt=none` ao Google, e as respostas dele também voltam: sem sessão
 *  lá, várias contas abertas sem `login_hint` que escolha uma, ou
 *  consentimento pendente. Todas viram a tela de entrada, sem aviso. */
const SILENT_LOGIN_REFUSALS = new Set([
  LOGIN_REQUIRED,
  "interaction_required",
  "account_selection_required",
  "consent_required"
]);

export function isSilentLoginRefusal(error: unknown): boolean {
  return error instanceof OidcError && error.code !== null && SILENT_LOGIN_REFUSALS.has(error.code);
}

function redirectUri(): string {
  return `${window.location.origin}${REDIRECT_PATH}`;
}

// ── descoberta ────────────────────────────────────────────────────────

type Endpoints = {
  authorization_endpoint: string;
  token_endpoint: string;
  end_session_endpoint?: string;
};

let discovered: Promise<Endpoints> | null = null;

/**
 * Os endpoints do realm, pelo documento de descoberta.
 *
 * Derivar os caminhos do issuer daria o mesmo resultado hoje (o Keycloak
 * os tem fixos), mas é a lib que decide isso, não a gente: se um dia o
 * realm ficar atrás de um caminho diferente, quem sabe disso é o próprio
 * servidor. A promessa é memoizada, e não o resultado — duas telas
 * pedindo ao mesmo tempo compartilham UMA requisição.
 */
function endpoints(): Promise<Endpoints> {
  if (!discovered) {
    discovered = fetch(`${ISSUER}/.well-known/openid-configuration`)
      .then(response => {
        if (!response.ok) throw new OidcError("O serviço de autenticação não respondeu.");
        return response.json() as Promise<Endpoints>;
      })
      .catch(error => {
        // Sem memoizar a falha: um Keycloak que piscou não pode deixar a
        // aba inteira sem login até alguém recarregar.
        discovered = null;
        throw error instanceof OidcError ? error : new OidcError("O serviço de autenticação não respondeu.");
      });
  }
  return discovered;
}

// ── PKCE ──────────────────────────────────────────────────────────────

function base64url(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = "";
  for (const byte of view) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomString(): string {
  return base64url(crypto.getRandomValues(new Uint8Array(32)));
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64url(digest);
}

// ── início do login ───────────────────────────────────────────────────

/**
 * Guarda o endereço de onde a pessoa veio, pra o deep link sobreviver ao
 * login. Vai pro `sessionStorage` pelo mesmo motivo do verifier: precisa
 * atravessar o redirect, é de uso único (`consumeReturnPath`) e não abre
 * sessão nenhuma sozinho. A raiz não precisa ser guardada, e o callback
 * NUNCA pode virar destino — seria um laço.
 */
function rememberReturnPath(): void {
  const { pathname, search, hash } = window.location;
  if (pathname === REDIRECT_PATH || pathname === "/") return;
  window.sessionStorage.setItem(RETURN_TO_KEY, `${pathname}${search ?? ""}${hash ?? ""}`);
}

/**
 * Lê E apaga o destino guardado por `beginLogin`. Só devolve caminho
 * relativo à mesma origem: o valor vem de storage e vai parar em
 * `navigate`, então `https://evil` e `//evil` (protocol-relative) são
 * recusados — senão o login viraria um open redirect.
 */
export function consumeReturnPath(): string | null {
  const value = window.sessionStorage.getItem(RETURN_TO_KEY);
  window.sessionStorage.removeItem(RETURN_TO_KEY);
  if (!value || !value.startsWith("/") || value.startsWith("//")) return null;
  return value;
}

/**
 * Manda o navegador pro Keycloak.
 *
 * `silent: true` acrescenta `prompt=none`: o Keycloak não desenha tela
 * nenhuma — ou devolve um código (o cookie de SSO valia) ou volta com
 * `error=login_required`. É o que faz recarregar a página não pedir
 * senha de novo, agora que token não mora mais no `localStorage`.
 */
export async function beginLogin(
  options: {
    silent?: boolean;
    /** `kc_idp_hint`: leva direto ao provedor externo (o Google), sem a
     *  tela do Keycloak. É o caminho padrão do login por pessoa. */
    idpHint?: string | null;
    /** `prompt=login`: ignora a sessão de SSO que já existe. Desde
     *  2026-10-21 só a porta das exceções por senha usa (não vai ao
     *  Google); o clique pelo Google usa `reauthenticate`. */
    forceLogin?: boolean;
    /** `prompt=select_account`: "entrar com outra conta" (2026-10-15). Com
     *  `kc_idp_hint`, o Keycloak repassa o `prompt` ao Google — quando o
     *  provedor no realm não fixa um próprio — e o Google mostra a escolha
     *  de conta em vez de seguir com a que está aberta no navegador. Só
     *  funciona DEPOIS de encerrar a sessão no Keycloak (`switchAccount`):
     *  com ela aberta, o Keycloak a reaproveitaria sem ir ao Google. */
    selectAccount?: boolean;
    /** `max_age=0`: o Keycloak exige autenticação nova e ignora a sessão de
     *  SSO, como o `prompt=login` — mas SEM repassar nada ao Google
     *  (2026-10-21). O `prompt` do client chega ao Google sempre que o
     *  provedor no realm não fixa um próprio, e `prompt=login` lá pode
     *  pedir a senha do Google de novo; o `max_age` só iria com "Pass
     *  max_age", que o provedor Google não tem (conferido contra o realm).
     *  É o que o clique em "Entrar com a conta Google" usa, junto com
     *  `selectAccount`. */
    reauthenticate?: boolean;
    /** `login_hint`: a conta que o Google deve usar (2026-10-21). Com
     *  `prompt=none`, é o que deixa o Google escolher sozinho quando há
     *  várias contas abertas no navegador. O Keycloak só a repassa com
     *  "Pass login_hint" ligado no provedor (KEYCLOAK.md). */
    loginHint?: string | null;
    /** `kc_action`: a pessoa já está logada e vai ao realm SÓ pra trocar a
     *  senha ou configurar o autenticador, na tela com a cara do
     *  ReputAItion, e volta pra página de onde saiu (2026-10-14). É o que
     *  substitui o painel da conta do Keycloak, que só a TI vê. */
    action?: AccountAction;
  } = {}
): Promise<void> {
  if (!oidcEnabled) throw new OidcError("Login pelo Keycloak não está configurado.");

  const verifier = randomString();
  const state = randomString();
  // O `nonce` (2026-10-21) volta DENTRO do `id_token`: amarra o token a
  // esta ida, como o `state` amarra a resposta. Ver `completeLogin`.
  const nonce = randomString();
  // SEM `try/catch` de propósito, aqui e nos outros acessos ao
  // `sessionStorage` deste módulo (registrado em 2026-10-25, achado 29 da
  // revisão de 2026-10-24): `code_verifier`, `state` e `nonce` PRECISAM
  // atravessar o redirect, e sem storage o login não tem como funcionar.
  // Falhar alto aqui, antes de ir ao Keycloak, é certo; engolir deixaria
  // a pessoa voltar sem o verifier e tomar um erro pior na troca do
  // código. Diferente do `localStorage` de preferência de tela, que é
  // conveniência e degrada em silêncio.
  window.sessionStorage.setItem(VERIFIER_KEY, verifier);
  window.sessionStorage.setItem(STATE_KEY, state);
  window.sessionStorage.setItem(NONCE_KEY, nonce);
  rememberReturnPath();

  const { authorization_endpoint } = await endpoints();
  const params = new URLSearchParams({
    client_id: CLIENT_ID as string,
    redirect_uri: redirectUri(),
    response_type: "code",
    scope: SCOPE,
    state,
    nonce,
    code_challenge: await challengeFor(verifier),
    code_challenge_method: "S256",
    // A tela do Keycloak em português, sempre — e não o idioma do
    // NAVEGADOR, que é o que ele usa sozinho. Medido contra o realm da
    // AWS: num navegador em inglês a tela vinha "Sign in to your
    // account", e a pessoa caía num sistema em português logo depois. A
    // tela de entrada tem que combinar com o aplicativo, não com a
    // configuração da máquina de quem entra.
    //
    // Só funciona porque o realm declara `pt-BR` em `supportedLocales`;
    // idioma que ele não tem, ele ignora e cai no padrão dele.
    ui_locales: "pt-BR"
  });
  if (options.silent) params.set("prompt", "none");
  else if (options.selectAccount) params.set("prompt", "select_account");
  else if (options.forceLogin) params.set("prompt", "login");
  if (options.reauthenticate) params.set("max_age", "0");
  if (options.idpHint) params.set("kc_idp_hint", options.idpHint);
  if (options.loginHint) params.set("login_hint", options.loginHint);
  if (options.action) params.set("kc_action", options.action);

  window.location.assign(`${authorization_endpoint}?${params}`);
}

/** Já tentamos o login silencioso nesta aba? Guarda contra o laço de
 *  redirect: sem isso, um `login_required` mandaria tentar de novo, pra
 *  sempre, sem nada na tela além de um piscar. */
export function silentLoginAlreadyTried(): boolean {
  return window.sessionStorage.getItem(SILENT_TRIED_KEY) === "1";
}

export function markSilentLoginTried(): void {
  window.sessionStorage.setItem(SILENT_TRIED_KEY, "1");
}

export function forgetSilentLoginAttempt(): void {
  window.sessionStorage.removeItem(SILENT_TRIED_KEY);
}

/**
 * O e-mail da última entrada pelo Google neste navegador (2026-10-21).
 *
 * **É também a licença da entrada automática.** O login silencioso só vai
 * até o Google quando há um e-mail lembrado — e ele some ao sair, ao
 * trocar de conta e quando o acesso é recusado. Sem isso, "Sair" num
 * computador compartilhado não sairia: o próximo carregamento acharia a
 * sessão do Google ainda aberta e entraria de novo, sozinho. Quem nunca
 * entrou neste navegador também não é levado ao Google sem pedir (a
 * decisão de 2026-10-15 vale até o primeiro clique).
 *
 * Não é segredo nem abre sessão sozinho: é o que o Google mostraria na
 * escolha de conta. Sem storage, a entrada automática só não acontece.
 */
export function rememberedLoginHint(): string | null {
  try {
    return window.localStorage.getItem(LOGIN_HINT_KEY) || null;
  } catch {
    return null;
  }
}

export function rememberLoginHint(email: string): void {
  try {
    window.localStorage.setItem(LOGIN_HINT_KEY, email);
  } catch {
    // Sem storage, a pessoa entra pelo clique, como antes.
  }
}

export function forgetLoginHint(): void {
  try {
    window.localStorage.removeItem(LOGIN_HINT_KEY);
  } catch {
    // Sem storage, não havia o que esquecer.
  }
}

// ── volta do Keycloak ─────────────────────────────────────────────────

export function isAuthorizationResponse(): boolean {
  if (window.location.pathname !== REDIRECT_PATH) return false;
  const params = new URLSearchParams(window.location.search);
  return params.has("code") || params.has("error");
}

/**
 * Troca o código por tokens.
 *
 * A comparação de `state` é o que impede alguém de fazer o seu navegador
 * completar um login que ele começou (CSRF de login): o valor foi
 * sorteado por esta aba, guardado aqui, e não sai daqui. Consumido de
 * uma vez, junto com o verificador — se o mesmo código chegar duas
 * vezes, a segunda não tem com que ser trocado.
 *
 * **O `nonce` do `id_token` tem que ser o desta ida** (2026-10-21). O
 * `state` prova que a RESPOSTA é desta aba; o `nonce` prova que o
 * `id_token` foi emitido PARA ela — um `id_token` de outro login, enfiado
 * na resposta, traria outro `nonce`. Sem `id_token` também é recusa: o
 * escopo pede `openid`, e o Keycloak sempre o manda.
 */
export async function completeLogin(): Promise<TokenSet> {
  const params = new URLSearchParams(window.location.search);
  const verifier = window.sessionStorage.getItem(VERIFIER_KEY);
  const expectedState = window.sessionStorage.getItem(STATE_KEY);
  const expectedNonce = window.sessionStorage.getItem(NONCE_KEY);
  window.sessionStorage.removeItem(VERIFIER_KEY);
  window.sessionStorage.removeItem(STATE_KEY);
  window.sessionStorage.removeItem(NONCE_KEY);

  const error = params.get("error");
  if (error) throw new OidcError(params.get("error_description") || "Não foi possível entrar.", error);

  const code = params.get("code");
  if (!code) throw new OidcError("A resposta do login veio sem código.");
  if (!verifier || !expectedNonce) throw new OidcError("O login expirou nesta aba. Tente de novo.");
  if (!expectedState || params.get("state") !== expectedState) {
    throw new OidcError("A resposta do login não corresponde a esta aba.");
  }

  const tokens = await exchange({
    grant_type: "authorization_code",
    code,
    redirect_uri: redirectUri(),
    code_verifier: verifier
  });
  const nonce = tokens.idToken ? decodeJwtPayload(tokens.idToken)?.nonce : undefined;
  if (nonce !== expectedNonce) {
    throw new OidcError("A resposta do login não corresponde a esta aba.");
  }
  return tokens;
}

export async function refreshTokens(refreshToken: string): Promise<TokenSet> {
  return exchange({ grant_type: "refresh_token", refresh_token: refreshToken });
}

/**
 * O POST no endpoint de token. Sem `client_secret`: o cliente é PÚBLICO,
 * e um segredo embutido num SPA é um segredo publicado.
 *
 * A folga de 30s no vencimento não é enfeite — sem ela, uma requisição
 * disparada no último segundo chega ao servidor com o token já vencido,
 * e o sintoma é um 401 intermitente que ninguém reproduz.
 */
async function exchange(body: Record<string, string>): Promise<TokenSet> {
  const { token_endpoint } = await endpoints();
  const response = await fetch(token_endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ ...body, client_id: CLIENT_ID as string })
  });

  const payload = (await response.json().catch(() => null)) as
    | { access_token?: string; refresh_token?: string; id_token?: string; expires_in?: number; error?: string; error_description?: string }
    | null;

  if (!response.ok || !payload?.access_token) {
    throw new OidcError(payload?.error_description || "Não foi possível concluir o login.", payload?.error ?? null);
  }

  const lifetime = typeof payload.expires_in === "number" ? payload.expires_in : 300;
  return {
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token ?? null,
    idToken: payload.id_token ?? null,
    expiresAt: Date.now() + Math.max(lifetime - 30, 5) * 1000
  };
}

// ── leitura dos claims, só pra exibição ───────────────────────────────

/**
 * Nome, e-mail e papéis do `id_token`.
 *
 * **Decodifica, não valida** — ver o cabeçalho do módulo. O `sub` daqui
 * é o mesmo que o `/api/me` devolve, e é o do servidor que manda: este
 * serve pra desenhar a topbar antes mesmo de a primeira chamada voltar.
 *
 * Duas armadilhas de decodificação, e as duas produzem sintoma na tela.
 * `atob` não lê base64url nem tolera padding ausente, que é exatamente o
 * formato de um JWT — daí a tradução antes. E o que ele devolve são
 * BYTES: lê-los como texto direto transforma "Luíza" em "LuÃ­za" na
 * topbar, porque o JSON do token é UTF-8 e cada byte viraria um
 * caractere. `TextDecoder` faz a leitura certa.
 */
export function readIdentityClaims(idToken: string): IdentityClaims | null {
  try {
    const claims = decodeJwtPayload(idToken);
    if (!claims) return null;
    const sub = claims.sub;
    if (typeof sub !== "string" || !sub) return null;
    const resourceAccess = claims.resource_access as
      | Record<string, { roles?: unknown }>
      | undefined;
    const clientRoles = ROLES_CLIENT_ID ? resourceAccess?.[ROLES_CLIENT_ID]?.roles : undefined;
    const roles = Array.isArray(clientRoles)
      ? clientRoles.filter((role): role is string => typeof role === "string")
      : [];
    return {
      sub,
      name: typeof claims.name === "string" ? claims.name : null,
      email: typeof claims.email === "string" ? claims.email : null,
      roles
    };
  } catch {
    // Token ilegível não derruba a sessão: quem decide se ele vale é o
    // servidor, e a topbar sabe viver sem nome.
    return null;
  }
}

/** O payload de um JWT, sem conferir assinatura (ver o cabeçalho do
 *  módulo), ou `null` se ilegível. Serve à topbar e ao `nonce`. */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
    const bytes = Uint8Array.from(atob(padded), character => character.charCodeAt(0));
    const claims = JSON.parse(new TextDecoder().decode(bytes)) as unknown;
    return claims && typeof claims === "object" ? (claims as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

// ── saída ─────────────────────────────────────────────────────────────

/**
 * Encerra a sessão NO KEYCLOAK, não só nesta aba.
 *
 * Limpar o estado local sozinho seria um "sair" que não sai: o cookie de
 * SSO continuaria valendo, e o `prompt=none` do próximo carregamento
 * traria a pessoa de volta logada — que é o pior desfecho possível pra
 * quem clicou em Sair num computador compartilhado.
 */
/**
 * "Entrar com outra conta" (2026-10-15): encerra a sessão no Keycloak e
 * deixa marcado, nesta aba, que a volta deve abrir a escolha de conta
 * (`consumeSwitchAccount` + `selectAccount`). É o caminho de quem foi
 * recusado com a conta errada — sem encerrar, o Keycloak devolvia a
 * mesma conta, e a mesma recusa, a cada clique.
 *
 * Não encerra a sessão do GOOGLE no navegador (isso tiraria a pessoa do
 * Gmail também): quem mostra a escolha é o `prompt=select_account`.
 */
export async function switchAccount(idToken: string | null): Promise<void> {
  window.sessionStorage.setItem(SWITCH_ACCOUNT_KEY, "1");
  await endSession(idToken);
}

/** A volta do `switchAccount`? Lê e apaga a marca (vale uma vez). */
export function consumeSwitchAccount(): boolean {
  const pending = window.sessionStorage.getItem(SWITCH_ACCOUNT_KEY) === "1";
  window.sessionStorage.removeItem(SWITCH_ACCOUNT_KEY);
  return pending;
}

export async function endSession(idToken: string | null): Promise<void> {
  forgetSilentLoginAttempt();
  // Sair (e trocar de conta) desliga a entrada automática pelo Google:
  // a sessão de lá continua aberta, e sem isto ela traria a pessoa de
  // volta no próximo carregamento.
  forgetLoginHint();
  const { end_session_endpoint } = await endpoints().catch(() => ({}) as Endpoints);
  if (!end_session_endpoint) {
    window.location.assign("/");
    return;
  }
  const params = new URLSearchParams({ post_logout_redirect_uri: window.location.origin });
  // Sem `id_token_hint` o Keycloak exibe uma tela de confirmação — o que
  // parece detalhe e é a diferença entre sair e "quase sair".
  if (idToken) params.set("id_token_hint", idToken);
  else params.set("client_id", CLIENT_ID as string);
  window.location.assign(`${end_session_endpoint}?${params}`);
}
